import { type UserDetail } from '@utazzo/shared';
import { UzButton } from '@utazzo/ui';
import { KeyRound, Pencil } from 'lucide-react';
import React, { useState } from 'react';

import { useSendPasswordReset } from '../use-users';
import { ProfileRow, ROLE_LABELS, UserSalesCoverageRows } from '../user-display';

import { UserFormDrawer } from './UserFormDrawer';

import { SoftPill } from '@/components/soft-pill';

interface UserProfilePanelProps {
  user: UserDetail;
}

export const UserProfilePanel = ({ user }: UserProfilePanelProps): React.JSX.Element => {
  const [editOpen, setEditOpen] = useState(false);
  const sendReset = useSendPasswordReset();

  return (
    <>
      <section className="flex flex-col gap-4">
        <div className="flex items-center justify-end gap-2">
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => sendReset.mutate(user.id)}
            loading={sendReset.isPending}
          >
            <KeyRound className="size-4" /> Send password reset
          </UzButton>
          <UzButton size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="size-4" /> Edit
          </UzButton>
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-4">
          <div className="mb-2 text-sm font-semibold text-zinc-900">Identity</div>
          <ProfileRow label="Full name" value={user.fullName} />
          <ProfileRow label="Email" value={user.email} />
          <ProfileRow label="Phone" value={user.phone} />
          <ProfileRow label="Employee code" value={user.employeeCode} />
          <ProfileRow label="Primary role" value={ROLE_LABELS[user.role] ?? user.role} />
          {user.assignedRoles.length > 0 && (
            <ProfileRow
              label="Assigned roles"
              value={
                <div className="flex flex-wrap gap-1">
                  {user.assignedRoles.map((r) => (
                    <SoftPill key={r.id} className="bg-zinc-100 text-zinc-700">
                      {r.name}
                    </SoftPill>
                  ))}
                </div>
              }
            />
          )}
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-4">
          <div className="mb-2 text-sm font-semibold text-zinc-900">Org placement</div>
          <ProfileRow label="Branch" value={user.branch?.name ?? null} />
          <ProfileRow label="Department" value={user.department?.name ?? null} />
          <ProfileRow label="Reports to" value={user.reportingManager?.fullName ?? null} />
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-4">
          <div className="mb-2 text-sm font-semibold text-zinc-900">Sales coverage</div>
          <UserSalesCoverageRows
            salesScopes={user.salesScopes}
            salesHauls={user.salesHauls}
            salesTripTypes={user.salesTripTypes}
            destinations={user.destinations}
          />
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-4">
          <div className="mb-2 text-sm font-semibold text-zinc-900">Account</div>
          <ProfileRow label="Active" value={user.isActive ? 'Yes' : 'No'} />
          <ProfileRow
            label="Must reset password"
            value={user.mustResetPassword ? 'Yes (welcome email pending)' : 'No'}
          />
          <ProfileRow
            label="Last login"
            value={user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
          />
          <ProfileRow label="Created" value={new Date(user.createdAt).toLocaleString()} />
          <ProfileRow label="Updated" value={new Date(user.updatedAt).toLocaleString()} />
          {user.deletedAt && (
            <ProfileRow label="Deleted" value={new Date(user.deletedAt).toLocaleString()} />
          )}
        </div>
      </section>
      <UserFormDrawer open={editOpen} onOpenChange={setEditOpen} mode="edit" user={user} />
    </>
  );
};
